/** @jsx jsx */

import {
  Fragment as _,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import getBlockById from 'services/apiHelpers/getBlockById';
import { Box } from '@chameleoncreator/creator-ui';
import { useCachedResource } from 'services/useCachedResource';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';

const Background = ({ background, backgroundImageOpacity }) => {
  const cachedBackgroundImage = useCachedResource(
    CHAMELEON_CACHE_v1,
    background
  );

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        ...(background && {
          background: `url('${cachedBackgroundImage ||
            background}') center / cover no-repeat`,
        }),

        opacity: backgroundImageOpacity / 100,
        pointerEvents: 'none',
      }}
    />
  );
};

const BackgroundWithParallax = props => {
  const {
    state,
    interactions,
    background,
    backgroundImageOpacity,
    parallaxValue = 10,
    width,
    height,
  } = props;

  const cachedBackgroundImage = useCachedResource(
    CHAMELEON_CACHE_v1,
    background
  );

  const [
    refreshParallaxScrollOnTopicChange,
    setRefreshParallaxScrollOnTopicChange,
  ] = useState<boolean>(false);

  const img = useRef<HTMLImageElement | null>(null);
  const setImageRef = useCallback(node => {
    img.current = node;
  }, []);

  const minHeight = `${100 + parallaxValue}vh`;

  useEffect(() => {
    if (!img.current) {
      return;
    }

    const parallaxThrottler = () => {
      // we can implement whatever more specific throttling approach...
      requestAnimationFrame(parallax);
    };

    const parallax = () => {
      if (!img.current) {
        return;
      }

      const { innerHeight, scrollY } = window;
      const imgTravel = img.current.offsetHeight - innerHeight;
      const scrollTravel = innerHeight * (interactions.length - 1);
      const scrollUnit = imgTravel / scrollTravel;
      const scrollValue = scrollY * scrollUnit;

      if (scrollValue > imgTravel) {
        return;
      }

      //
      img.current.style.transform = `translateY(-${scrollValue}px)`;
    };
    //

    window.addEventListener('scroll', parallaxThrottler);
    window.addEventListener('resize', parallaxThrottler);
    parallax();

    return () => {
      window.removeEventListener('scroll', parallaxThrottler);
      window.removeEventListener('resize', parallaxThrottler);
    };
  }, [setImageRef, refreshParallaxScrollOnTopicChange]);

  useEffect(() => {
    if (!img.current) {
      return;
    }

    let tick;

    if (document.body.scrollHeight > window.innerHeight) {
      const imgHeightOnLoad = img.current.offsetHeight;

      let checkLimit = 0;

      tick = setInterval(() => {
        if (!img.current) {
          return;
        }

        if (img.current.offsetHeight !== imgHeightOnLoad) {
          clearInterval(tick);
          setRefreshParallaxScrollOnTopicChange(true);
        }

        //
        if (checkLimit >= 300) {
          clearInterval(tick);
        }
        checkLimit += 1;
        //
      }, 1);
    }

    return () => {
      clearInterval(tick);
    };
  }, [setImageRef, setRefreshParallaxScrollOnTopicChange]);

  return (
    <_>
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <img
          ref={setImageRef}
          src={cachedBackgroundImage || background}
          style={{
            all: 'initial',
            position: 'absolute',
            objectFit: 'cover',
            minWidth: '100vw',
            minHeight,
            opacity: backgroundImageOpacity / 100,
          }}
        />
      </div>
    </_>
  );
};

const GradientBackground = ({ backgroundGradientData }) => {
  const {
    backgroundPrimaryColor,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
  } = backgroundGradientData || {};

  return (
    <div
      css={css({
        position: 'fixed',
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${backgroundPrimaryColor})`,
      })}
    />
  );
};

const TopicContainer = props => {
  const { state, topic, children } = props as any;

  const block: any = getBlockById(topic.id);

  let {
    src,
    imageId,
    width,
    height,
    backgroundImage,
    backgroundImageOpacity = 100,
    parallax = false,
    parallaxValue = 10,
    backgroundGradient,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
    backgroundGradientFullHeight = false,
  }: any = block.props;

  const main = useRef<HTMLDivElement>(null);

  // dirty gotta try and cover for legacy backgroundImage
  if (src) {
    backgroundImage = src;
  }

  let backgroundColor =
    block.props.backgroundColor || state.theme.backgroundColor || '';

  if (backgroundGradient && backgroundGradientFullHeight) {
    backgroundColor = `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${block.props.backgroundColor})`;
  }

  return (
    <Box
      ref={main}
      className="fader_-5s"
      id="main"
      tabindex="-1"
      css={css`
        position: relative;
        background: ${backgroundColor};
        ${backgroundGradient &&
          backgroundGradientFullHeight &&
          `
        background-size: 100vw
          ${`calc(100vh * ${children?.props?.interactions.length})`};
          `}
      `}
    >
      {backgroundGradient && !backgroundGradientFullHeight && (
        <GradientBackground
          backgroundGradientData={{
            backgroundGradientAngle,
            backgroundPrimaryColor: backgroundColor,
            backgroundSecondaryColor,
          }}
        />
      )}

      {window.dev() || !parallax ? (
        <Background
          background={backgroundImage}
          backgroundImageOpacity={backgroundImageOpacity}
        />
      ) : (
        <BackgroundWithParallax
          state={state}
          interactions={children.props.interactions}
          background={backgroundImage}
          backgroundImageOpacity={backgroundImageOpacity}
          parallaxValue={parallaxValue}
          width={width}
          height={height}
        />
      )}

      {children}
    </Box>
  );
};

export default TopicContainer;
